import * as React from 'react';

/**
 * Hook that handles click-outside detection for dropdowns, modals, etc.
 * Adds a mousedown listener when isActive is true, and removes it on cleanup.
 *
 * @param ref - Ref(s) to the container element(s) to detect clicks outside of
 * @param callback - Function to call when a click outside is detected
 * @param isActive - Whether the listener should be active
 */
export function useClickOutside(
  ref: React.RefObject<Element | null> | React.RefObject<Element | null>[],
  callback: () => void,
  isActive: boolean
): void {
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const refs = Array.isArray(ref) ? ref : [ref];
      const isOutside = refs.every((r) => {
        return !r.current?.contains(target);
      });
      if (isOutside) {
        callback();
      }
    };

    if (isActive) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isActive, ref, callback]);
}
